import { CRMAccountSettings } from '@alucio/aws-beacon-amplify/src/models';

import {
  CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE,
} from './CRMAccountSearchIndex';
import { AccountSearchBackfillMarker, CRMAccountSearchDB } from './CRMAccountSearchDB';
import { CRMAccount } from './CRMIndexedDBTypes';
import * as logger from 'src/utils/logger';

type AccountSearchBackfillChunk = {
  accounts: CRMAccount[];
  lastAccountId?: string;
  hasMoreAccounts: boolean;
};

type CRMAccountSearchSyncerConfig = {
  accountSearchDB: Pick<
    CRMAccountSearchDB,
    | 'getIndexedTokenCount'
    | 'getBackfillMarker'
    | 'getBackfillConfigHash'
    | 'clearIndex'
    | 'putAccountSearchRows'
    | 'setBackfillMarker'
  >;
  getAccountConfig: () => Promise<CRMAccountSettings | null>;
  getAccountCount: () => Promise<number>;
  readAccountSearchBackfillChunk: (lastAccountId?: string) => Promise<AccountSearchBackfillChunk>;
  isStorageQuotaLimitReached: () => Promise<boolean>;
};

type CRMAccountSearchSyncOptions = {
  forceRebuild?: boolean;
};

export class CRMAccountSearchSyncer {
  private accountSearchDB: CRMAccountSearchSyncerConfig['accountSearchDB'];
  private getAccountConfig: CRMAccountSearchSyncerConfig['getAccountConfig'];
  private getAccountCount: CRMAccountSearchSyncerConfig['getAccountCount'];
  private readAccountSearchBackfillChunk: CRMAccountSearchSyncerConfig['readAccountSearchBackfillChunk'];
  private isStorageQuotaLimitReached: CRMAccountSearchSyncerConfig['isStorageQuotaLimitReached'];

  constructor(config: CRMAccountSearchSyncerConfig) {
    this.accountSearchDB = config.accountSearchDB;
    this.getAccountConfig = config.getAccountConfig;
    this.getAccountCount = config.getAccountCount;
    this.readAccountSearchBackfillChunk = config.readAccountSearchBackfillChunk;
    this.isStorageQuotaLimitReached = config.isStorageQuotaLimitReached;
  }

  private getCompletedBackfillState = async (
    indexedCount: number,
    backfillMarker: AccountSearchBackfillMarker | null,
  ) => {
    const shouldReadAccountCount =
      !backfillMarker ||
      backfillMarker.status !== CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE ||
      backfillMarker.accountCount != null;
    const accountCount = shouldReadAccountCount
      ? await this.getAccountCount()
      : backfillMarker.accountCount;
    const hasCompletedBackfill = backfillMarker?.status === CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE &&
      (backfillMarker.accountCount == null || backfillMarker.accountCount === accountCount);

    return {
      accountCount,
      hasCompletedBackfill: Boolean(indexedCount && hasCompletedBackfill),
    };
  };

  private getBackfillConfig = async (backfillMarker: AccountSearchBackfillMarker | null) => {
    const config = await this.getAccountConfig();
    const configHash = this.accountSearchDB.getBackfillConfigHash(config);
    const hasStaleConfigBackfill = Boolean(
      backfillMarker?.configHash &&
      backfillMarker.configHash !== configHash,
    );

    return {
      config,
      configHash,
      hasStaleConfigBackfill,
    };
  };

  sync = async (options: CRMAccountSearchSyncOptions = {}): Promise<void> => {
    const indexedCount = await this.accountSearchDB.getIndexedTokenCount();
    const backfillMarker = indexedCount
      ? await this.accountSearchDB.getBackfillMarker()
      : null;
    const { accountCount, hasCompletedBackfill } = await this.getCompletedBackfillState(
      indexedCount,
      backfillMarker,
    );

    let config: CRMAccountSettings | null = null;
    let configHash = '';
    let hasStaleConfigBackfill = false;

    if (backfillMarker?.configHash) {
      ({ config, configHash, hasStaleConfigBackfill } = await this.getBackfillConfig(backfillMarker));
    }

    if (hasCompletedBackfill && !hasStaleConfigBackfill && !options.forceRebuild) {
      return;
    }

    const canResumeBackfill = Boolean(
      indexedCount &&
      backfillMarker?.status === 'in_progress' &&
      !options.forceRebuild &&
      !hasStaleConfigBackfill,
    );

    if (!config) {
      ({ config, configHash } = await this.getBackfillConfig(backfillMarker));
    }

    if (
      options.forceRebuild ||
      hasStaleConfigBackfill ||
      (indexedCount && !hasCompletedBackfill && !canResumeBackfill)
    ) {
      await this.accountSearchDB.clearIndex();
    }

    const isQuotaExceeded = await this.isStorageQuotaLimitReached();
    if (isQuotaExceeded) {
      throw new Error('Storage quota limit reached before CRM account search backfill');
    }

    let indexedTokenCount = 0;
    let backfilledAccountCount = canResumeBackfill ? backfillMarker?.processedCount || 0 : 0;
    let scannedAccountCount = 0;
    let lastAccountId = canResumeBackfill ? backfillMarker?.lastAccountId : undefined;
    let hasMoreAccounts = true;

    while (hasMoreAccounts) {
      const backfillChunk = await this.readAccountSearchBackfillChunk(lastAccountId);
      lastAccountId = backfillChunk.lastAccountId;
      hasMoreAccounts = backfillChunk.hasMoreAccounts;
      scannedAccountCount += backfillChunk.accounts.length;

      backfilledAccountCount += backfillChunk.accounts.length;
      indexedTokenCount += await this.accountSearchDB.putAccountSearchRows(backfillChunk.accounts, config);

      if (lastAccountId) {
        await this.accountSearchDB.setBackfillMarker({
          status: hasMoreAccounts ? 'in_progress' : CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE,
          lastAccountId,
          processedCount: backfilledAccountCount,
          accountCount,
          configHash,
        });
      }
    }

    if (!scannedAccountCount) {
      await this.accountSearchDB.setBackfillMarker({
        status: CRM_ACCOUNT_SEARCH_BACKFILL_MARKER_VALUE,
        lastAccountId,
        processedCount: backfilledAccountCount,
        accountCount,
        configHash,
      });
      return;
    }

    logger.CRMMachine.debug('Backfilled CRM account search tokens', {
      accounts: backfilledAccountCount,
      tokens: indexedTokenCount,
    });
  };
}
